/**
 * Cost Report Analysis tab — lawyer-first.
 *
 * For each HCRIS Worksheet A cost center, shows where this facility's
 * per-resident-day cost falls on the distribution of OTHER facilities, as a
 * bell curve with the facility marked in the tail and a plain-English
 * percentile. A declared related party is a separate flag. Click a cost center
 * to overlay the All / RP-only / No-RP curves (do RP facilities cost more, and
 * is this one high even among them?).
 *
 * Year selector: "All Years" shows a cost-center × year status grid (a row that
 * stays red is a systemic over-cost); a specific year shows the bell curves.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, CircularProgress, Tooltip, Chip, Divider } from '@mui/material';
import api from '../../api/axios';
import { curveFromQuantiles } from './costReportCurve';

interface Dist { p10: number | null; p25: number | null; p30: number | null; median: number | null; p70: number | null; p75: number | null; p90: number | null; count: number | null; }
interface YearCell {
  has_rp: boolean; total: number | null; resident_days: number | null; beds?: number | null; unit?: string; facility_value: number | null; status: 'red' | 'yellow' | 'green' | null; segment: string | null;
  percentiles: { all: number | null; rp_only: number | null; no_rp: number | null };
  distribution: { all: Dist | null; rp_only: Dist | null; no_rp: Dist | null };
}
interface CostCenter { key: string; label: string; unit?: string; years: Record<string, YearCell>; }
interface Payload { ccn: string; state: string | null; urban_rural: string | null; years: number[]; unit: string; cost_centers: CostCenter[]; }

const STATUS_COLOR: Record<string, string> = { red: '#c62828', yellow: '#ef6c00', green: '#2e7d32' };
const GROUP_COLOR = { all: '#78909c', rp_only: '#8e24aa', no_rp: '#00897b' };
const money = (v: number | null | undefined) => (v == null ? '—' : `$${v.toFixed(2)}`);
const perUnit = (u?: string) => (u === 'per_bed' ? '/bed' : '/res-day');
const usd = (v: number | null | undefined) => (v == null ? '—' : (Math.abs(v) >= 1e6 ? `$${(v / 1e6).toFixed(2)}M` : Math.abs(v) >= 1e3 ? `$${Math.round(v / 1e3)}K` : `$${Math.round(v)}`));
const ordinal = (n: number) => { const s = ['th', 'st', 'nd', 'rd'], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };
const pctlText = (p: number | null | undefined) => (p == null ? '—' : `${ordinal(Math.min(99, Math.round(p)))} pctile`);

// Distribution curve comes from the shared module (smooth monotone-cubic through
// the robust quantiles) so the facility + owner-group tabs never drift.

interface CurveGroup { key: 'all' | 'rp_only' | 'no_rp'; name: string; d: Dist | null; pctile: number | null; }

/** Bell curve(s) on a shared x-scale with the facility marker in the tail. */
const BellCurve: React.FC<{ groups: CurveGroup[]; value: number | null; status: string | null; width: number; height: number; showThresholds?: boolean; visibleKeys?: string[] }> = ({ groups, value, status, width: W, height: H, showThresholds, visibleKeys }) => {
  const p90s = groups.map((g) => g.d?.p90 ?? 0).filter((x) => x > 0);
  if (!p90s.length) return <Typography variant="caption" color="text.disabled">no benchmark</Typography>;
  const domain = Math.max(...p90s) * 1.55 || 1;
  const xp = (v: number | null | undefined) => (v == null ? null : Math.min(W, Math.max(0, (v / domain) * W)));
  const all = groups.find((g) => g.key === 'all')?.d || null;
  const overflow = value != null && value > domain;
  const markerX = value == null ? null : overflow ? W : xp(value)!;
  const mColor = status ? STATUS_COLOR[status] : '#212121';

  return (
    <svg width={W} height={H + 2} style={{ display: 'block', overflow: 'visible' }}>
      {/* baseline */}
      <line x1={0} y1={H} x2={W} y2={H} stroke="#e0e0e0" strokeWidth={1} />
      {/* curves */}
      {groups.map((g) => {
        if (visibleKeys && !visibleKeys.includes(g.key)) return null;
        const paths = g.d ? curveFromQuantiles(g.d, domain, W, H) : null; if (!paths) return null;
        const c = GROUP_COLOR[g.key];
        return (
          <g key={g.key}>
            {g.key === 'all' && <path d={paths.area} fill={c} fillOpacity={0.16} />}
            <path d={paths.line} fill="none" stroke={c} strokeWidth={1.75} strokeDasharray={g.key === 'rp_only' ? '5 3' : g.key === 'no_rp' ? '1.5 2.5' : undefined} />
          </g>
        );
      })}
      {/* thresholds from All: p70 thin orange, p90 thicker red */}
      {showThresholds && all && [{ v: all.p70, c: '#ef6c00', lbl: 'p70', w: 1.25, dash: '3 3' }, { v: all.p90, c: '#c62828', lbl: 'p90', w: 2.5, dash: '6 3' }].map((t, i) => t.v != null && xp(t.v) != null && (
        <g key={i}>
          <line x1={xp(t.v)!} y1={H * 0.14} x2={xp(t.v)!} y2={H} stroke={t.c} strokeWidth={t.w} strokeDasharray={t.dash} opacity={0.9} />
          {H > 60 && <text x={xp(t.v)!} y={H * 0.11} textAnchor="middle" fontSize={10} fontWeight={800} fill={t.c}>{t.lbl}</text>}
        </g>
      ))}
      {/* facility marker */}
      {markerX != null && (
        <g>
          <line x1={markerX} y1={-1} x2={markerX} y2={H} stroke={mColor} strokeWidth={2.5} />
          {overflow && <text x={W - 2} y={9} textAnchor="end" fontSize={10} fontWeight={800} fill={mColor}>▸</text>}
        </g>
      )}
    </svg>
  );
};

/** Box-and-whisker companion to the density curve, in real $/resident-day: box =
 *  p30–p70 (middle of the market), whiskers = p10–p90, line = median, with the
 *  facility marker (labeled with its dollar value) spanning every row. One row per
 *  visible series on a shared dollar axis, so an RP-only box sitting right of the
 *  No-RP box shows related-party facilities cost more — and the marker past the
 *  box (p70) / whisker (p90) reproduces the green/amber/red zones without density. */
const niceStep = (raw: number) => { const p = Math.pow(10, Math.floor(Math.log10(raw || 1))); const n = (raw || 1) / p; return (n < 1.5 ? 1 : n < 3 ? 2 : n < 7 ? 5 : 10) * p; };
const fmtAxis = (v: number) => (v >= 10 ? `$${Math.round(v)}` : `$${v.toFixed(v % 1 === 0 ? 0 : 1)}`);

const BoxWhisker: React.FC<{ groups: CurveGroup[]; value: number | null; status: string | null; width: number; height: number; visibleKeys?: string[] }> = ({ groups, value, status, width: W, height: H, visibleKeys }) => {
  const shown = groups.filter((g) => g.d && g.d.p90 != null && (!visibleKeys || visibleKeys.includes(g.key)));
  const p90s = shown.map((g) => g.d!.p90 ?? 0).filter((x) => x > 0);
  if (!p90s.length) return <Typography variant="caption" color="text.disabled">no benchmark</Typography>;
  const mL = 58, mR = 18, mT = 22, mB = 26;
  const plotW = Math.max(40, W - mL - mR);
  const plotH = Math.max(40, H - mT - mB);
  // Extend the domain to include the facility value so its real amount is always
  // on-scale (no overflow). p90*1.4 keeps the boxes from being squashed otherwise.
  const domain = Math.max(Math.max(...p90s) * 1.4, (value ?? 0) * 1.08) || 1;
  const xp = (v: number | null | undefined) => (v == null ? null : mL + Math.min(plotW, Math.max(0, (v / domain) * plotW)));
  const rowH = plotH / shown.length;
  const boxH = Math.min(22, rowH * 0.46);
  const mColor = status ? STATUS_COLOR[status] : '#212121';
  const markerX = value == null ? null : xp(value)!;
  const step = niceStep(domain / 4);
  const ticks: number[] = []; for (let t = 0; t <= domain * 1.0001; t += step) ticks.push(t);

  return (
    <svg width={W} height={H} style={{ display: 'block', overflow: 'visible' }}>
      {/* dollar axis */}
      <line x1={mL} y1={mT + plotH} x2={mL + plotW} y2={mT + plotH} stroke="#e0e0e0" strokeWidth={1} />
      {ticks.map((t, i) => { const x = xp(t)!; return (
        <g key={i}>
          <line x1={x} y1={mT + plotH} x2={x} y2={mT + plotH + 3} stroke="#bdbdbd" strokeWidth={1} />
          <text x={x} y={mT + plotH + 15} textAnchor="middle" fontSize={9.5} fill="#9e9e9e">{fmtAxis(t)}</text>
        </g>
      ); })}
      {/* facility marker + its real dollar value */}
      {markerX != null && (
        <g>
          <line x1={markerX} y1={mT - 2} x2={markerX} y2={mT + plotH} stroke={mColor} strokeWidth={2.5} />
          <text x={markerX} y={mT - 7} textAnchor="middle" fontSize={11.5} fontWeight={800} fill={mColor}>{money(value)}</text>
        </g>
      )}
      {/* one box-and-whisker row per visible series */}
      {shown.map((g, i) => {
        const d = g.d!;
        const cy = mT + i * rowH + rowH / 2;
        const c = GROUP_COLOR[g.key];
        const x10 = xp(d.p10), x30 = xp(d.p30), x50 = xp(d.median), x70 = xp(d.p70), x90 = xp(d.p90);
        return (
          <g key={g.key}>
            <text x={0} y={cy + 3} fontSize={10.5} fontWeight={700} fill="#555">{g.name}</text>
            {x10 != null && x90 != null && <line x1={x10} y1={cy} x2={x90} y2={cy} stroke={c} strokeWidth={1} />}
            {x10 != null && <line x1={x10} y1={cy - 5} x2={x10} y2={cy + 5} stroke={c} strokeWidth={1} />}
            {x90 != null && <line x1={x90} y1={cy - 5} x2={x90} y2={cy + 5} stroke={c} strokeWidth={1} />}
            {x30 != null && x70 != null && <rect x={x30} y={cy - boxH / 2} width={Math.max(1, x70 - x30)} height={boxH} fill={c} fillOpacity={0.16} stroke={c} strokeWidth={1.5} rx={2} />}
            {x50 != null && <line x1={x50} y1={cy - boxH / 2 - 1} x2={x50} y2={cy + boxH / 2 + 1} stroke={c} strokeWidth={2.25} />}
          </g>
        );
      })}
    </svg>
  );
};

/** Measure an element's content width (responsive SVG sizing) via a callback ref
 *  + ResizeObserver, so it works even when the node mounts later (on expand). */
function useElementWidth(): [(el: HTMLDivElement | null) => void, number] {
  const [w, setW] = useState(0);
  const roRef = React.useRef<ResizeObserver | null>(null);
  const ref = React.useCallback((el: HTMLDivElement | null) => {
    roRef.current?.disconnect();
    if (el) {
      const ro = new ResizeObserver((entries) => { for (const e of entries) setW(Math.round(e.contentRect.width)); });
      ro.observe(el);   // fires an initial callback with the content-box width
      roRef.current = ro;
    }
  }, []);
  return [ref, w];
}

/** Compact status grid for the "All Years" view. */
const Heatmap: React.FC<{ centers: CostCenter[]; years: number[]; onPick: (y: number) => void }> = ({ centers, years, onPick }) => (
  <Box sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ borderBottom: '2px solid #e0e0e0', backgroundColor: '#fafbfc' }}>
          <th style={{ padding: '6px 10px', textAlign: 'left', fontSize: '0.74rem', color: '#555' }}>Cost Center</th>
          {years.map((y) => <th key={y} style={{ padding: '6px 4px', fontSize: '0.72rem', color: '#777', fontWeight: 700, cursor: 'pointer' }} onClick={() => onPick(y)}>{String(y).slice(2)}</th>)}
        </tr>
      </thead>
      <tbody>
        {centers.map((cc) => (
          <tr key={cc.key} style={{ borderBottom: '1px solid #f3f3f3' }}>
            <td style={{ padding: '5px 10px', fontSize: '0.82rem', fontWeight: 600 }}>{cc.label}</td>
            {years.map((y) => {
              const c = cc.years[String(y)];
              const bg = c?.status ? STATUS_COLOR[c.status] : '#eceff1';
              return (
                <td key={y} style={{ padding: '3px 4px', textAlign: 'center' }}>
                  <Tooltip arrow title={c ? `${y}: ${money(c.facility_value)}${perUnit(c.unit)} · ${c.percentiles.all != null ? pctlText(c.percentiles.all) : (c.status ?? 'no data')}${c.has_rp ? ' · RP' : ''}` : `${y}: no data`}>
                    <Box onClick={() => onPick(y)} sx={{ position: 'relative', width: 30, height: 22, mx: 'auto', borderRadius: 0.5, backgroundColor: bg, cursor: 'pointer' }}>
                      {c?.has_rp && <Box sx={{ position: 'absolute', top: 2, right: 2, width: 5, height: 5, borderRadius: '50%', backgroundColor: c.status ? 'rgba(255,255,255,0.9)' : '#90a4ae' }} />}
                    </Box>
                  </Tooltip>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </Box>
);

/** One cost center in a specific year: headline + bell curve, expandable to 3 groups. */
const CostCard: React.FC<{ cc: CostCenter; year: number }> = ({ cc, year }) => {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState<Record<string, boolean>>({ all: true, rp_only: true, no_rp: true });
  const [densRef, densW] = useElementWidth();
  const [boxRef, boxW] = useElementWidth();
  const cell = cc.years[String(year)];
  if (!cell) return null;
  const groups: CurveGroup[] = [
    { key: 'all', name: 'All', d: cell.distribution.all, pctile: cell.percentiles.all },
    { key: 'rp_only', name: 'RP-only', d: cell.distribution.rp_only, pctile: cell.percentiles.rp_only },
    { key: 'no_rp', name: 'No-RP', d: cell.distribution.no_rp, pctile: cell.percentiles.no_rp },
  ];
  const hasData = cell.facility_value != null;
  const pcolor = cell.status ? STATUS_COLOR[cell.status] : 'text.secondary';

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: hasData ? 'pointer' : 'default' }} onClick={() => hasData && setOpen(!open)}>
        <Box sx={{ flex: '0 0 230px', minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 700 }}>{cc.label}</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
            <Tooltip arrow title={cell.total == null ? '' : (cell.unit === 'per_bed' ? (cell.beds ? `${usd(cell.total)} ÷ ${cell.beds.toLocaleString()} beds` : '') : (cell.resident_days ? `${usd(cell.total)} ÷ ${cell.resident_days.toLocaleString()} resident-days` : ''))}>
              <strong style={{ color: '#333' }}>{usd(cell.total)}</strong>
            </Tooltip>
            <span> · {money(cell.facility_value)}{perUnit(cell.unit)}</span>
            {cell.has_rp && <Chip label="RP" size="small" color="warning" variant="outlined" sx={{ height: 16, ml: 0.75, '& .MuiChip-label': { px: 0.5, fontSize: '0.62rem', fontWeight: 700 } }} />}
          </Typography>
        </Box>
        <Box sx={{ flex: '0 0 240px' }}>
          {hasData ? <BellCurve groups={[groups[0]]} value={cell.facility_value} status={cell.status} width={240} height={42} showThresholds /> : <Typography variant="caption" color="text.disabled">no cost reported</Typography>}
        </Box>
        <Box sx={{ flex: 1, textAlign: 'right' }}>
          {hasData && cell.percentiles.all != null ? (
            <>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: pcolor, lineHeight: 1.1 }}>{pctlText(cell.percentiles.all)}</Typography>
              <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                more than {Math.min(99, Math.round(cell.percentiles.all))}% of {cell.segment} facilities
              </Typography>
            </>
          ) : null}
        </Box>
      </Box>

      {open && hasData && (() => {
        const visKeys = groups.filter((g) => visible[g.key]).map((g) => g.key);
        return (
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
            {/* Left half: density — the shape of the market */}
            <Box ref={densRef} sx={{ flex: 1, minWidth: 0, pr: 3 }}>
              <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.3, mb: 0.75, textAlign: 'center' }}>Cost distribution — shape of the market</Typography>
              {densW > 0 && <BellCurve groups={groups} value={cell.facility_value} status={cell.status} width={Math.max(260, densW)} height={190} showThresholds visibleKeys={visKeys} />}
            </Box>
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'divider' }} />
            {/* Right half: box-and-whisker — real $/res-day and where this facility sits */}
            <Box ref={boxRef} sx={{ flex: 1, minWidth: 0, pl: 3 }}>
              <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.3, mb: 0.75, textAlign: 'center' }}>Spread &amp; position — $ / {cell.unit === 'per_bed' ? 'bed' : 'resident-day'}</Typography>
              {boxW > 0 && <BoxWhisker groups={groups} value={cell.facility_value} status={cell.status} width={Math.max(260, boxW)} height={190} visibleKeys={visKeys} />}
              <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled', textAlign: 'center', mt: 0.5 }}>box = p30–p70 · whiskers p10–p90 · line = median · ▮ this facility</Typography>
            </Box>
          </Box>
          {/* Clickable legend = show/hide filter for each series */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
            {groups.map((g) => {
              const on = visible[g.key];
              return (
                <Box key={g.key} onClick={(e) => { e.stopPropagation(); setVisible((v) => ({ ...v, [g.key]: !v[g.key] })); }}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.85, cursor: 'pointer', px: 1.25, py: 0.6, borderRadius: 1.5,
                        border: '1px solid', borderColor: on ? GROUP_COLOR[g.key] : 'divider',
                        backgroundColor: on ? `${GROUP_COLOR[g.key]}14` : 'transparent', opacity: on ? 1 : 0.45,
                        transition: 'opacity 0.15s', userSelect: 'none' }}>
                  <Box sx={{ width: 18, height: 0, borderTop: `2.5px ${g.key === 'rp_only' ? 'dashed' : g.key === 'no_rp' ? 'dotted' : 'solid'} ${GROUP_COLOR[g.key]}` }} />
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: on ? 'text.primary' : 'text.disabled' }}>
                    {g.name}{g.d?.count != null ? ` (${g.d.count})` : ''}
                  </Typography>
                  <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary' }}>
                    {g.pctile != null ? pctlText(g.pctile) : '—'} · med {money(g.d?.median)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', mt: 1, textAlign: 'center' }}>
            Click a series to show/hide it. Left: the cost curve, orange line = p70 (top 30% begins) · red line = p90 (top 10%). Right: box-and-whisker — the box is the middle of the market (p30–p70), whiskers reach p10–p90, and this facility's line past the box / whisker is what turns it amber / red. RP-only right of No-RP means related-party facilities cost more here.
          </Typography>
        </Box>
        );
      })()}
    </Box>
  );
};

const CostReportAnalysisTab: React.FC<{ ccn: string; state?: string; schemeId?: string }> = ({ ccn, state, schemeId }) => {
  const [data, setData] = useState<Payload | null>(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState<number | 'all'>('all');

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = state ? `?state=${encodeURIComponent(state)}` : '';
    api.get(`/schemes/${schemeId}/understaffing/facilities/${ccn}/cost-report-analysis/${params}`)
      .then((res) => { if (active) { setData(res.data); if (res.data?.years?.length) setSel(res.data.years[res.data.years.length - 1]); } })
      .catch((err) => console.error('Failed to fetch cost report analysis', err))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [ccn, state, schemeId]);

  const cardsForYear = useMemo(() => {
    if (!data || sel === 'all') return [];
    return [...data.cost_centers]
      .filter((cc) => cc.years[String(sel)]?.facility_value != null)
      .sort((a, b) => (b.years[String(sel)]?.percentiles.all ?? -1) - (a.years[String(sel)]?.percentiles.all ?? -1));
  }, [data, sel]);

  // Cost centers with no reported cost for this facility/year — shown as a
  // footnote so it's clear all cost centers are captured, not missing.
  const notReported = useMemo(() => {
    if (!data || sel === 'all') return [] as string[];
    return data.cost_centers.filter((cc) => cc.years[String(sel)]?.facility_value == null).map((cc) => cc.label);
  }, [data, sel]);

  if (loading && !data) return <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}><CircularProgress size={32} /></Box>;
  if (!data || !data.cost_centers.length) return <Typography color="text.secondary" sx={{ p: 2 }}>No cost report data available for this facility.</Typography>;

  const chip = (label: string, val: number | 'all') => (
    <Box onClick={() => setSel(val)} sx={{
      px: 1.5, py: 0.4, borderRadius: 5, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
      backgroundColor: sel === val ? 'primary.main' : 'grey.100', color: sel === val ? '#fff' : 'text.primary',
    }}>{label}</Box>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        {chip('All Years', 'all')}
        {data.years.map((y) => <React.Fragment key={y}>{chip(String(y), y)}</React.Fragment>)}
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', ml: 1 }}>
          vs all facilities in {data.state}{data.urban_rural ? ` (${data.urban_rural === 'R' ? 'rural' : 'urban'})` : ''}
        </Typography>
      </Box>

      {sel === 'all' ? (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Cost vs all facilities, by year — green ≤ p70, amber p70–p90, red &gt; p90. A row that stays red is a <strong>systemic</strong> over-cost; the dot marks a declared related party. Click a year to see the bell curves.
          </Typography>
          <Heatmap centers={data.cost_centers} years={data.years} onPick={(y) => setSel(y)} />
        </>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            FY {sel} · where this facility falls vs all facilities (worst first). Click a cost center to compare <strong>All / RP-only / No-RP</strong>.
          </Typography>
          {cardsForYear.length ? cardsForYear.map((cc) => <CostCard key={cc.key} cc={cc} year={sel as number} />)
            : <Typography color="text.secondary" sx={{ p: 2 }}>No positive cost-center data for FY {sel}.</Typography>}
          {notReported.length > 0 && (
            <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled', mt: 1.5 }}>
              {notReported.length} of {data.cost_centers.length} cost centers not separately reported on this facility's FY {sel} cost report (no cost on that Worksheet A line): {notReported.join(', ')}.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default CostReportAnalysisTab;
